import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';

/**
 * Reading of the refusals `reinstateMembership` answers with. The wording is ours.
 *
 * `PORTAL_CLOSED_ON_ACCOUNT` is about the account, not the person: it was closed to the
 * portal, and it has to be reopened first.
 *
 * `PRIVILEGE_ESCALATION` is about the role, not the gesture: reopening restores the role,
 * and that role is beyond what the actor may grant. Told the wrong way round, the operator
 * asks for a right to reopen that does not exist instead of handing the membership over.
 */
export const reinstateRefusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'MEMBERSHIP_NOT_REVOKED':
            return {
                title: c('Reinstate refusal').t`This access is not closed.`,
                guidance: c('Reinstate refusal')
                    .t`There is nothing to reopen: it has been reopened since this page was loaded, or it never was closed. Reload the sheet to read its current state.`,
            };
        case 'PORTAL_CLOSED_ON_ACCOUNT':
            return {
                title: c('Reinstate refusal').t`This account is no longer open to the portal.`,
                guidance: c('Reinstate refusal')
                    .t`It was closed since this access was revoked, and reopening the access would grant an entry nobody can use. Nothing is wrong with this person: the account has to be opened to the portal again first, from its own sheet.`,
            };
        case 'ACCOUNT_DISABLED':
            return {
                title: c('Reinstate refusal').t`This person's account is disabled.`,
                guidance: c('Reinstate refusal')
                    .t`A disabled person is refused whatever their accesses say, so reopening one would change nothing on screen. Reactivating their account is a separate gesture, and it comes first.`,
            };
        case 'PRIVILEGE_ESCALATION':
            return {
                title: c('Reinstate refusal').t`You cannot grant the role this access carries.`,
                guidance: c('Reinstate refusal')
                    .t`Reopening restores the role as it was — the gesture takes no role of its own — and restoring a role is granting it. This one reaches beyond your own rights, so the refusal is about the role, not about reopening: hand the gesture to somebody who carries it, or have the role changed first.`,
            };
        case 'DIFFERENT_ROOT_ORGANIZATION':
            return {
                title: c('Reinstate refusal').t`This person now belongs to another client.`,
                guidance: c('Reinstate refusal')
                    .t`They joined a different client group while this access was closed, and a person belongs to a single one. Reopening here would attach them to two: their memberships on the other client have to be settled first.`,
            };
        case 'MEMBERSHIP_NOT_FOUND':
            return {
                title: c('Reinstate refusal').t`This access no longer exists.`,
                guidance: c('Reinstate refusal')
                    .t`It has disappeared since this page was opened. Go back to the list: the sheet you are reading is stale.`,
            };
        case 'MISSING_PERMISSION':
            // Rendered after the click: `viewer` exposes the role but not the
            // permissions, so no screen can pre-empt it.
            return {
                title: c('Reinstate refusal').t`You are not allowed to administer this person.`,
                guidance: c('Reinstate refusal')
                    .t`Reopening an access requires the right to manage the users of the account it belongs to, which your role does not carry. Ask for that right, or hand the gesture to whoever administers the account.`,
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Reinstate refusal').t`This account is outside your portfolio.`,
                guidance: c('Reinstate refusal')
                    .t`The access belongs to an account that is not entrusted to you. Ask for that account to be added to your portfolio, or hand the gesture over.`,
            };
        default:
            return {
                title: c('Reinstate refusal').t`The access was not reopened.`,
                guidance: c('Reinstate refusal')
                    .t`This refusal is not one the screen knows how to read. Nothing has changed: the access is still closed exactly as it was. The server's answer is below — it is what the support desk needs.`,
                // Kept only for an unknown code: without it the operator has nothing to report.
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};
